// packages/web/src/lib/retired.ts — S11 Part B: a retired record leaves the
// active lists.
//
// THE HOLE. Huan's 2026-08-12 ruling replaced Delete with Retire, and Retire
// sets `status = 'archived'`. But core's listRecords excludes only
// `status = 'deleted'` (packages/core/src/records/records.ts:1124) and
// nothing in packages/web read `status` at all — so Delete used to make a
// record vanish and Retire does not. A retired tech sat in the roster with
// no marker, which makes the new affordance look broken and is worse than
// what it replaced.
//
// WHY THE WEB AND NOT THE SERVER. Checked first, as the spec required: the
// list route's query schema (packages/server/src/plugins/records.ts's
// listQuerySchema) accepts type_key / lifecycle_state / include_deleted /
// limit / offset, and core's ListRecordsArgs has no status filter of any
// kind — only `includeDeleted`. There is no server-side status filter to
// use, and adding a route is outside this chunk. So the filter is here, and
// this comment is the record of that: the honest fix is a `status=` filter
// on GET /records (and on ListRecordsArgs), at which point these callers
// should ask the server instead of paging rows in to drop them.
//
// The cost while it lives here: the page (listAllRecordsByType) is over ALL
// non-deleted rows, so a tenant with many retired records pays to fetch
// them before we hide them. Ether Inc's scale makes that free; it is stated
// rather than discovered.
import type { RecordRow } from './api';

/** Retired = archived. 'deleted' never reaches a client (core filters it),
 * so in practice this reads as "not archived" — written against the whole
 * status vocabulary anyway, so a status added later is not silently active. */
export function isActiveRecord(row: RecordRow): boolean {
  return row.status === 'active';
}

export function isRetiredRecord(row: RecordRow): boolean {
  return row.status === 'archived';
}

/** The two halves of a list, in one pass and in the caller's own order.
 * Callers render `active` and keep `retired` REACHABLE — retiring is not
 * hiding: "a tech who leaves keeps their history and their payroll" (wf02). */
export function splitRetired(rows: RecordRow[]): { active: RecordRow[]; retired: RecordRow[] } {
  const active: RecordRow[] = [];
  const retired: RecordRow[] = [];
  for (const row of rows) (isRetiredRecord(row) ? retired : active).push(row);
  return { active, retired };
}

/** Just the active rows — for a surface that counts or sums (a retired work
 * order still sitting in an "awaiting payment" total is the same lie in
 * arithmetic form). */
export function activeOnly(rows: RecordRow[]): RecordRow[] {
  return rows.filter(isActiveRecord);
}
